/*
 * Todolist du mois (onglet Communications → À faire).
 * Le calcul est partagé avec le récap envoyé le 1er du mois : supabase/functions/_shared/crmTodo.ts.
 */
import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import {
  Banknote, BookOpenCheck, CalendarClock, CalendarPlus, Check, CheckCircle2, UserPlus, ChevronDown, ChevronRight, ExternalLink, FileText, Hourglass,
  Inbox, Landmark, Loader2, Mail, PartyPopper, Receipt, RefreshCw, RotateCcw, Send, Zap,
} from 'lucide-react';
import type { CrmContact, SharingOperation } from './types';
import {
  fetchTodoDone, fetchTodoParticipants, fetchTodoSettings, saveTodoSettings, sendDigestNow, sendInvitation, setTodoDone, todoContacts,
  updateContact,
} from './api';
import { displayName } from './types';
import {
  BILLING_CHANNELS, BILLING_CHANNEL_LABELS, type BillingChannel, MAX_DEFERRALS, type TodoDone, type TodoItem, type TodoParticipant, type TodoSettings,
  addMonths, computeTodo, currentMonth, monthLabel, todoCount,
} from '../../../supabase/functions/_shared/crmTodo';
import { errorMessage } from './ui';
import { SendDocumentsDialog } from './SendDocumentsDialog';
import { fetchNetAmount, isSendableDocument } from './accounting';
import {
  type BillingInvoice, type BillingParticipant, type BillingRow, type BillingState, analyzeBillingItem, fetchBillingParticipants,
  fetchBillingState, formatEuro, invoiceKey,
} from './billing';
import { BillingRunDialog } from './BillingRunDialog';
import { SepaFollowUp } from './SepaFollowUp';
import { type MemberTask, TASK_BADGES, TASK_KIND_LABELS, fetchOpenMemberTasks, setMemberTaskDone } from './memberTasks';

const CHANNEL_ICONS = { peppol: BookOpenCheck, email: Mail, direct_debit: Landmark } as const;
const CHANNEL_BUTTONS: Record<BillingChannel, string> = { peppol: 'Facturer via Accountable', email: 'Facturer par e-mail', direct_debit: 'Facturer par domiciliation' };
const CHANNEL_HELP: Record<BillingChannel, string> = {
  peppol: 'Sociétés : PDF à Accountable (revenue@). E-mail au membre si coché à l’envoi (par défaut : nouveaux membres).',
  email: 'PDF à Accountable (revenue@). E-mail au membre si coché à l’envoi (par défaut : nouveaux membres).',
  direct_debit: 'Particuliers consommateurs avec mandat actif : PDF à Accountable, e-mail de pré-notification au membre (toujours), fichier XML pour Easy Banking Business.',
};

interface Props {
  contacts: CrmContact[];
  operations: SharingOperation[];
  onOpenContact: (id: string) => void;
  /** Fiche modifiée depuis la todo (ex. invitation envoyée). */
  onContactUpdated: (c: CrmContact) => void;
}

const DONE_LABEL: Record<string, string> = { report: 'Envoyé', reimbursement: 'Remboursé', invoice: 'Facturé' };

/*
 * Solde des prosumers (facture si positif, décompte si négatif) : calculé dans le navigateur avec le même calcul que le PDF,
 * gardé 1 heure (comme les données de la todolist) ; « Actualiser » recalcule.
 */
type NetResult = { amount: number } | { error: string };
const NET_CACHE_KEY = 'crm-todo-prosumer-net-v1';
const netKey = (i: TodoItem) => `${i.ref}|${i.period}`;
function readNetCache(): Record<string, { amount: number; at: number }> {
  try {
    const raw = JSON.parse(localStorage.getItem(NET_CACHE_KEY) ?? '{}') as Record<string, { amount: number; at: number }>;
    return Object.fromEntries(Object.entries(raw).filter(([, v]) => v && Number.isFinite(v.amount) && Date.now() - v.at < CACHE_TTL_NET));
  } catch { return {}; }
}
function writeNetCache(key: string, amount: number | null): void {
  try {
    const cur = readNetCache();
    if (amount === null) delete cur[key]; else cur[key] = { amount, at: Date.now() };
    localStorage.setItem(NET_CACHE_KEY, JSON.stringify(cur));
  } catch { /* stockage indisponible */ }
}
const CACHE_TTL_NET = 60 * 60 * 1000;

/*
 * Données de la todolist gardées en mémoire et dans le navigateur pendant 1 heure :
 * rouvrir l'onglet est instantané ; « Actualiser » recharge depuis Supabase.
 * Le calcul lui-même (computeTodo) est immédiat et suit les fiches CRM en direct.
 */
interface TodoData { participants: TodoParticipant[]; done: TodoDone[]; settings: TodoSettings; loadedAt: number }
const CACHE_KEY = 'crm-todo-cache-v2';
const CACHE_TTL = 60 * 60 * 1000;
let memoryCache: TodoData | null = null;

function readCache(): TodoData | null {
  if (memoryCache && Date.now() - memoryCache.loadedAt < CACHE_TTL) return memoryCache;
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw) as TodoData;
    if (!data?.loadedAt || Date.now() - data.loadedAt > CACHE_TTL || !Array.isArray(data.participants)) return null;
    memoryCache = data;
    return data;
  } catch {
    return null;
  }
}

function writeCache(data: TodoData): void {
  memoryCache = data;
  try { localStorage.setItem(CACHE_KEY, JSON.stringify(data)); } catch { /* stockage indisponible : mémoire seulement */ }
}

export function TodoTab({ contacts, operations, onOpenContact, onContactUpdated }: Props) {
  const [data, setData] = useState<TodoData | null>(() => readCache());
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  /* Demandes des membres (onglet Contrat du dashboard) : chargées à chaque ouverture, hors cache. */
  const [memberTasks, setMemberTasks] = useState<MemberTask[] | null>(null);
  const loadMemberTasks = useCallback(() => {
    fetchOpenMemberTasks().then(setMemberTasks).catch(err => { setMemberTasks([]); toast.error(`Demandes des membres : ${errorMessage(err)}`); });
  }, []);
  useEffect(() => { loadMemberTasks(); }, [loadMemberTasks]);
  const completeTask = async (task: MemberTask) => {
    setMemberTasks(prev => (prev ?? []).filter(x => x.id !== task.id));
    try {
      await setMemberTaskDone(task.id, true);
      toast(tt => (
        <span className="flex items-center gap-3 text-sm">
          {task.title} : traité
          <button className="text-xs font-semibold text-amber-700 underline" onClick={async () => {
            toast.dismiss(tt.id);
            try { await setMemberTaskDone(task.id, false); loadMemberTasks(); } catch (err) { toast.error(errorMessage(err)); }
          }}>Annuler</button>
        </span>
      ), { duration: 6000 });
    } catch (err) {
      toast.error(errorMessage(err));
      loadMemberTasks();
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [participants, done, settings] = await Promise.all([fetchTodoParticipants(), fetchTodoDone(), fetchTodoSettings()]);
      const next = { participants, done, settings, loadedAt: Date.now() };
      setData(next);
      writeCache(next);
    } catch (err) {
      setLoadError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);
  // Chargement seulement si rien n'est en cache (ou si le cache a plus d'une heure).
  useEffect(() => { if (!data) load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /** Met à jour les données en mémoire et dans le cache. */
  const update = (patch: Partial<Pick<TodoData, 'done' | 'settings'>>) =>
    setData(prev => {
      if (!prev) return prev;
      const next = { ...prev, ...patch };
      writeCache(next);
      return next;
    });

  const settings = data?.settings ?? null;
  const done = data?.done ?? [];
  const month = currentMonth();
  const list = useMemo(() => (data
    ? computeTodo({ participants: data.participants, contacts: todoContacts(contacts), done: data.done, settings: data.settings, month })
    : null), [data, contacts, month]);
  const opName = (id: string | null) => operations.find(o => o.id === id)?.name ?? '';

  /* Prosumers prêts : solde calculé un par un, puis rangés en facturation (≥ 0) ou en remboursement (< 0). */
  const [netResults, setNetResults] = useState<Map<string, NetResult>>(() => {
    const cached = readNetCache();
    return new Map(Object.entries(cached).map(([k, v]) => [k, { amount: v.amount }]));
  });
  const [netRetry, setNetRetry] = useState(0);
  const prosumerReady = useMemo(() => list?.prosumers.ready ?? [], [list]);
  const prosumerSignature = prosumerReady.map(netKey).join(',');
  useEffect(() => {
    let cancelled = false;
    (async () => {
      for (const item of prosumerReady) {
        if (cancelled) return;
        const key = netKey(item);
        const known = netResults.get(key);
        if (known && 'amount' in known) continue;
        try {
          const amount = await fetchNetAmount(item);
          writeNetCache(key, amount);
          if (!cancelled) setNetResults(prev => new Map(prev).set(key, { amount }));
        } catch (err) {
          if (!cancelled) setNetResults(prev => new Map(prev).set(key, { error: errorMessage(err) }));
        }
      }
    })();
    return () => { cancelled = true; };
  }, [prosumerSignature, netRetry]); // eslint-disable-line react-hooks/exhaustive-deps
  const classified = useMemo(() => {
    const invoices: TodoItem[] = [];
    const reimbursements: TodoItem[] = [];
    const pending: TodoItem[] = [];
    const failed: { item: TodoItem; error: string }[] = [];
    for (const i of prosumerReady) {
      const res = netResults.get(netKey(i));
      if (!res) pending.push(i);
      else if ('error' in res) failed.push({ item: i, error: res.error });
      else (res.amount < 0 ? reimbursements : invoices).push({ ...i, kind: res.amount < 0 ? 'reimbursement' : 'invoice' });
    }
    return { invoices, reimbursements, pending, failed };
  }, [prosumerReady, netResults]);
  const netOf = (i: TodoItem) => { const r = netResults.get(netKey(i)); return r && 'amount' in r ? r.amount : null; };
  const reimbursementsReady = useMemo(() => [...(list?.reimbursements.ready ?? []), ...classified.reimbursements], [list, classified]);
  const deferredOf = (kind: 'invoice' | 'reimbursement') => (list?.prosumers.deferred ?? []).filter(i => (i.deferredKind ?? 'invoice') === kind);

  /* Facturation des consommateurs : canal, numéro, contrôles et factures déjà émises de chaque ligne prête. */
  const invoiceItems = useMemo(() => [...(list?.invoices.ready ?? []), ...classified.invoices], [list, classified]);
  const invoiceSignature = invoiceItems.map(i => `${i.ref}|${i.period}`).join(',');
  const [billingData, setBillingData] = useState<{ participants: Map<string, BillingParticipant>; state: BillingState } | null>(null);
  const [billingError, setBillingError] = useState<string | null>(null);
  const [billingReload, setBillingReload] = useState(0);
  useEffect(() => {
    if (!list) return;
    let cancelled = false;
    Promise.all([fetchBillingParticipants(invoiceItems), fetchBillingState(invoiceItems)])
      .then(([participants, state]) => { if (!cancelled) { setBillingData({ participants, state }); setBillingError(null); } })
      .catch(err => { if (!cancelled) setBillingError(errorMessage(err)); });
    return () => { cancelled = true; };
  }, [invoiceSignature, billingReload, !!list]); // eslint-disable-line react-hooks/exhaustive-deps
  const billingRows = useMemo(
    () => (billingData ? invoiceItems.map(i => analyzeBillingItem(i, contacts, billingData.participants, billingData.state)) : []),
    [billingData, invoiceItems, contacts],
  );
  /** Factures cochées pour l'envoi (rien n'est coché par défaut). */
  const [selectedInvoices, setSelectedInvoices] = useState<Set<string>>(new Set());
  /** Décomptes cochés pour l'envoi (rien n'est coché par défaut). */
  const [selectedStatements, setSelectedStatements] = useState<Set<string>>(new Set());
  const [billingRun, setBillingRun] = useState<{ channel: BillingChannel; rows: BillingRow[] } | null>(null);
  const [followUpOpen, setFollowUpOpen] = useState(false);
  const [followUpKey, setFollowUpKey] = useState(0);
  const mergeRecords = (records: BillingInvoice[]) => setBillingData(prev => {
    if (!prev || records.length === 0) return prev;
    const next = new Map(prev.state.records);
    for (const r of records) next.set(invoiceKey(r.entity_key, r.period), r);
    return { ...prev, state: { ...prev.state, records: next } };
  });

  const toggle = async (item: TodoItem, value: boolean) => {
    const key = { kind: item.kind, ref: item.ref, period: item.period };
    const same = (d: TodoDone) => d.kind === key.kind && d.ref === key.ref && d.period === key.period;
    update({ done: value ? [...done.filter(d => !same(d)), key] : done.filter(d => !same(d)) });
    try {
      await setTodoDone(key, value);
      if (value) {
        toast(t => (
          <span className="flex items-center gap-3 text-sm">
            {item.title} · {item.periodLabel} : {DONE_LABEL[item.kind]?.toLowerCase() ?? 'fait'}
            <button className="text-xs font-semibold text-amber-700 underline" onClick={() => { toast.dismiss(t.id); toggle(item, false); }}>Annuler</button>
          </span>
        ), { duration: 6000 });
      }
    } catch (err) {
      toast.error(errorMessage(err));
      load();
    }
  };

  /** Prosumer : la tâche revient le mois suivant avec un mois de données en plus (2 reports au plus). */
  const defer = async (item: TodoItem, undo = false) => {
    const kind = item.kind === 'reimbursement' ? 'defer_reimbursement' : 'defer_invoice';
    // Annulation depuis la liste des décalées : dernier mois reporté = celui qui précède le dernier mois de la tâche.
    const key = undo
      ? { kind: item.deferredKind === 'reimbursement' ? 'defer_reimbursement' : 'defer_invoice', ref: item.ref, period: addMonths(item.periodEnd, -1) }
      : { kind, ref: item.ref, period: item.periodEnd };
    const same = (d: TodoDone) => d.kind === key.kind && d.ref === key.ref && d.period === key.period;
    update({ done: undo ? done.filter(d => !same(d)) : [...done.filter(d => !same(d)), key] });
    try {
      await setTodoDone(key, !undo);
      if (!undo) {
        toast(t => (
          <span className="flex items-center gap-3 text-sm">
            {item.title} · {item.periodLabel} : décalé, revient avec {monthLabel(addMonths(item.periodEnd, 1))}
            <button className="text-xs font-semibold text-amber-700 underline" onClick={async () => {
              toast.dismiss(t.id);
              setData(prev => { if (!prev) return prev; const next = { ...prev, done: prev.done.filter(d => !same(d)) }; writeCache(next); return next; });
              try { await setTodoDone(key, false); } catch (err) { toast.error(errorMessage(err)); load(); }
            }}>Annuler</button>
          </span>
        ), { duration: 6000 });
      } else {
        toast.success(`${item.title} : report annulé`);
      }
    } catch (err) {
      toast.error(errorMessage(err));
      load();
    }
  };

  /** Remboursement marqué comme fait à la main (hors envoi par le CRM) : confirmation demandée. */
  const markReimbursed = (item: TodoItem) => {
    if (!window.confirm(`${item.title} · ${item.periodLabel}\n\nLa tâche va disparaître de la liste.\nLe décompte a-t-il bien été envoyé à Accountable ?`)) return;
    toggle(item, true);
  };

  /** Opérations incluses dans la todo (le même réglage sert au récap mensuel). */
  const isTracked = (id: string) => settings?.operations[id]?.tracked !== false;
  const toggleOperation = async (id: string) => {
    if (!settings) return;
    const current = settings.operations[id] ?? { tracked: true, invoicedThrough: null, reimbursedThrough: null };
    const next: TodoSettings = { ...settings, operations: { ...settings.operations, [id]: { ...current, tracked: !isTracked(id) } } };
    update({ settings: next });
    try {
      await saveTodoSettings(next);
    } catch (err) {
      toast.error(errorMessage(err));
      update({ settings });
    }
  };

  /** Invitation à la plateforme depuis la todo : e-mail du modèle « invitation », puis date d'envoi dans la fiche. */
  const [inviting, setInviting] = useState<string | null>(null);
  const invite = async (c: CrmContact) => {
    const question = c.invitation_sent_at
      ? `${displayName(c)} a déjà été invité le ${new Date(c.invitation_sent_at).toLocaleDateString('fr-BE')}. Renvoyer l’invitation à la plateforme à ${c.email} ?`
      : `Envoyer l’invitation à la plateforme à ${displayName(c)} (${c.email}) ?`;
    if (!window.confirm(question)) return;
    setInviting(c.id);
    try {
      await sendInvitation(c, operations);
      const updated = await updateContact(c.id, { invitation_sent_at: new Date().toISOString() });
      onContactUpdated(updated);
      toast.success(`Invitation envoyée à ${c.email}`);
    } catch (err) {
      toast.error(`Invitation : ${errorMessage(err)}`);
    } finally {
      setInviting(null);
    }
  };

  /** Envoi des factures / décomptes des sociétés (Accountable, e-mail au membre, ou les deux). */
  const [sendItems, setSendItems] = useState<TodoItem[] | null>(null);
  /** Tâche cochée par la fenêtre d'envoi (déjà enregistrée dans crm_todo_done). */
  const markDoneLocal = (item: TodoItem) => setData(prev => {
    if (!prev) return prev;
    const key = { kind: item.kind, ref: item.ref, period: item.period };
    const same = (d: TodoDone) => d.kind === key.kind && d.ref === key.ref && d.period === key.period;
    const next = { ...prev, done: [...prev.done.filter(d => !same(d)), key] };
    writeCache(next);
    return next;
  });

  /** Ouvre l'outil de factures groupées de l'opération, participants pré-cochés et période remplie. */
  const openInvoiceTool = (operationId: string, period: string, participantIds: string[]) => {
    try {
      localStorage.setItem('selected_sharing_operation_id', operationId);
      localStorage.setItem('crm_invoice_prefill', JSON.stringify({ operationId, startMonth: period, endMonth: period, participantIds, createdAt: Date.now() }));
    } catch {
      toast.error('Impossible de préparer l’outil de factures dans ce navigateur');
      return;
    }
    window.location.assign('/admin');
  };

  const sendNow = async () => {
    if (!window.confirm('Envoyer maintenant le récap du mois à info@sunisup.be ?')) return;
    setSending(true);
    try {
      const res = await sendDigestNow();
      toast.success(`Récap envoyé à ${res.to}`);
    } catch (err) {
      toast.error(`Récap : ${errorMessage(err)}`);
    } finally {
      setSending(false);
    }
  };

  /** Factures prêtes regroupées par opération et par mois : un bouton « Outil de factures » par groupe (aucun envoi). */
  const invoiceBatches = (() => {
    const map = new Map<string, { operationId: string; period: string; label: string; participantIds: string[]; count: number }>();
    for (const i of list?.invoices.ready ?? []) {
      if (!i.operationId) continue;
      const key = `${i.operationId}|${i.period}`;
      const b = map.get(key) ?? { operationId: i.operationId, period: i.period, label: i.periodLabel, participantIds: [], count: 0 };
      b.participantIds.push(...i.participantIds);
      b.count += 1;
      map.set(key, b);
    }
    return Array.from(map.values()).sort((a, b) => a.period.localeCompare(b.period));
  })();

  /** Décomptes prêts à l'envoi (sociétés : Accountable et/ou e-mail ; particuliers : e-mail). */
  const sendableStatements = reimbursementsReady.filter(isSendableDocument);
  const selectedStatementsList = sendableStatements.filter(i => selectedStatements.has(statementKey(i)));
  const selectableBillingKeys = billingRows.filter(r => r.issues.length === 0).map(r => r.key);

  if (!list) {
    return (
      <div className="flex-1 flex items-center justify-center p-6">
        {loadError ? (
          <div className="max-w-lg text-center space-y-3">
            <p className="text-sm font-semibold text-neutral-900">La todolist n’a pas pu être chargée</p>
            <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg p-3 text-left break-words">{loadError}</p>
            <button onClick={load} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-neutral-200 text-xs text-neutral-700 hover:bg-neutral-50">
              <RefreshCw className="w-3.5 h-3.5" />Réessayer
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-xs text-neutral-400"><Loader2 className="w-5 h-5 animate-spin" />Chargement de la todolist…</div>
        )}
      </div>
    );
  }

  const deferButton = (item: TodoItem) => item.prosumer && (item.deferrals ?? 0) < MAX_DEFERRALS && (
    <button onClick={() => defer(item)}
      title={`La tâche disparaît et revient en ${monthLabel(addMonths(item.periodEnd, 1))} avec ${(item.deferrals ?? 0) + 2} mois de données (${MAX_DEFERRALS - (item.deferrals ?? 0)} report${MAX_DEFERRALS - (item.deferrals ?? 0) > 1 ? 's' : ''} possible${MAX_DEFERRALS - (item.deferrals ?? 0) > 1 ? 's' : ''})`}
      className="flex items-center gap-1 text-[11px] text-neutral-500 hover:text-neutral-900 whitespace-nowrap">
      <CalendarPlus className="w-3.5 h-3.5" />Décaler au mois suivant
    </button>
  );
  const prosumerBadges = (item: TodoItem) => item.prosumer && (
    <>
      <span className="ml-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-sky-50 text-sky-800">prosumer</span>
      {(item.deferrals ?? 0) > 0 && (
        <span className="ml-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800">
          décalée {item.deferrals} fois · {item.deferrals! + 1} mois de données
        </span>
      )}
      {netOf(item) !== null && <span className="ml-1.5 text-[10px] text-neutral-500">solde {formatEuro(netOf(item)!)}</span>}
    </>
  );

  const row = (item: TodoItem, opts: { check?: boolean; open?: boolean; invite?: boolean; send?: boolean; select?: boolean; reimbursed?: boolean } = {}) => (
    <li key={`${item.kind}|${item.ref}|${item.period}`} className="flex items-start gap-3 px-4 py-2.5">
      {opts.select && (
        <input type="checkbox" className="mt-1" checked={selectedStatements.has(statementKey(item))} aria-label={`Inclure ${item.title}`}
          onChange={() => setSelectedStatements(prev => toggleIn(prev, statementKey(item)))} />
      )}
      {opts.check && (
        <button onClick={() => toggle(item, true)} title={`Marquer comme ${DONE_LABEL[item.kind]?.toLowerCase()}`}
          className="mt-0.5 w-4 h-4 rounded border border-neutral-300 hover:border-emerald-500 hover:bg-emerald-50 shrink-0 flex items-center justify-center group"
          aria-label={`Marquer ${item.title} comme ${DONE_LABEL[item.kind]?.toLowerCase()}`}>
          <Check className="w-3 h-3 text-emerald-600 opacity-0 group-hover:opacity-100" />
        </button>
      )}
      <div className="min-w-0 flex-1">
        <p className="text-sm text-neutral-900">
          <span className="font-medium">{item.title}</span>
          {item.periodLabel && <span className="text-neutral-500"> · {item.periodLabel}</span>}
          {item.overdue && <span className="ml-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800">en retard</span>}
          {prosumerBadges(item)}
        </p>
        <p className="text-[11px] text-neutral-500 truncate" title={item.eans.length ? item.eans.join(', ') : undefined}>
          {item.subtitle}{opName(item.operationId) && operations.filter(o => isTracked(o.id)).length > 1 ? ` · ${opName(item.operationId)}` : ''}
          {item.eans.length > 1 && <span className="text-neutral-400"> · {item.eans.slice(0, 3).join(', ')}{item.eans.length > 3 ? '…' : ''}</span>}
        </p>
      </div>
      {opts.invite && item.contactId && (() => {
        const c = contacts.find(x => x.id === item.contactId);
        if (!c?.email?.trim()) return null;
        return (
          <button onClick={() => invite(c)} disabled={inviting === c.id}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-[#F8BD3D] hover:bg-[#E5A935] text-[11px] font-semibold text-neutral-900 whitespace-nowrap disabled:opacity-50">
            {inviting === c.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
            {c.invitation_sent_at ? 'Renvoyer l’invitation' : 'Envoyer l’invitation'}
          </button>
        );
      })()}
      {opts.send && isSendableDocument(item) && (
        <button onClick={() => setSendItems([item])}
          title={item.companyNumber ? 'Envoyer à Accountable, au membre par e-mail, ou les deux' : 'Particulier : envoi par e-mail'}
          className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-neutral-300 hover:border-neutral-900 text-[11px] font-semibold text-neutral-800 whitespace-nowrap">
          <Send className="w-3.5 h-3.5" />{item.kind === 'reimbursement' ? 'Envoyer le décompte' : 'Envoyer la facture'}
        </button>
      )}
      {deferButton(item)}
      {opts.open && item.contactId && (
        <button onClick={() => onOpenContact(item.contactId!)} className="flex items-center gap-1 text-[11px] text-neutral-500 hover:text-neutral-900 whitespace-nowrap">
          Ouvrir la fiche<ExternalLink className="w-3 h-3" />
        </button>
      )}
      {opts.reimbursed && (
        <button onClick={() => markReimbursed(item)} title="Retire la tâche sans envoi par le CRM (confirmation demandée)"
          className="flex items-center gap-1 text-[11px] text-neutral-400 hover:text-emerald-700 whitespace-nowrap">
          <CheckCircle2 className="w-3.5 h-3.5" />Marquer comme fait
        </button>
      )}
    </li>
  );

  /** Prosumer décalé : en attente du mois ajouté ; le report peut être annulé. */
  const deferredRow = (item: TodoItem) => (
    <li key={`d|${item.ref}|${item.period}`} className="flex items-start gap-3 px-4 py-2 text-[11px]">
      <div className="min-w-0 flex-1">
        <p className="text-neutral-800"><span className="font-medium">{item.title}</span><span className="text-neutral-500"> · {item.periodLabel}</span></p>
        <p className="text-neutral-500">
          Décalée {item.deferrals} fois{item.missingMonth ? ` · revient dans la liste dès réception des données (${monthLabel(item.missingMonth)})` : ''}
        </p>
      </div>
      <button onClick={() => defer(item, true)} className="flex items-center gap-1 text-neutral-500 hover:text-neutral-900 whitespace-nowrap">
        <RotateCcw className="w-3.5 h-3.5" />Annuler le report
      </button>
    </li>
  );
  const prosumerStatus = (
    <>
      {classified.pending.length > 0 && (
        <li className="flex items-center gap-1.5 px-4 py-2 text-[11px] text-neutral-500">
          <Loader2 className="w-3.5 h-3.5 animate-spin" />Calcul du solde de {classified.pending.length} prosumer{classified.pending.length > 1 ? 's' : ''} (facture ou remboursement)…
        </li>
      )}
      {classified.failed.map(({ item, error }) => (
        <li key={`f|${item.ref}|${item.period}`} className="flex items-start gap-3 px-4 py-2 text-[11px] bg-red-50/50">
          <p className="flex-1 text-red-700"><span className="font-medium">{item.title}</span> · {item.periodLabel} : solde non calculé ({error})</p>
          <button onClick={() => { setNetResults(prev => { const n = new Map(prev); n.delete(netKey(item)); return n; }); setNetRetry(v => v + 1); }}
            className="flex items-center gap-1 text-neutral-600 hover:text-neutral-900 whitespace-nowrap"><RefreshCw className="w-3.5 h-3.5" />Réessayer</button>
        </li>
      ))}
    </>
  );

  const billingRowView = (r: BillingRow) => {
    const { item, record } = r;
    const selectable = r.issues.length === 0;
    const checked = selectable && selectedInvoices.has(r.key);
    const progress = record && (record.accountable_sent_at || record.member_sent_at)
      ? [
        record.accountable_sent_at ? 'Accountable envoyé' : 'Accountable à envoyer',
        r.channel === 'peppol' && !record.copy_to_member ? null : record.member_sent_at ? 'membre prévenu' : 'membre à prévenir',
        r.channel === 'direct_debit' && record.member_sent_at && !record.sepa_batch_id && Number(record.amount) > 0 ? 'fichier XML à créer' : null,
      ].filter(Boolean).join(' · ')
      : null;
    return (
      <li key={r.key} className={`flex items-start gap-3 px-4 py-2.5 ${selectable ? '' : 'bg-red-50/50'}`}>
        <input type="checkbox" className="mt-1" checked={checked} disabled={!selectable} aria-label={`Inclure ${item.title}`}
          onChange={() => setSelectedInvoices(prev => toggleIn(prev, r.key))} />
        <div className="min-w-0 flex-1">
          <p className="text-sm text-neutral-900">
            <span className="font-medium">{item.title}</span>
            <span className="text-neutral-500"> · {item.periodLabel}</span>
            {record && <span className="ml-1.5 font-mono text-[10px] text-neutral-500">{record.invoice_number}</span>}
            {record?.amount !== null && record?.amount !== undefined && <span className="ml-1.5 text-[10px] text-neutral-500">{formatEuro(Number(record.amount))}</span>}
            {prosumerBadges(item)}
          </p>
          <p className="text-[11px] text-neutral-500 truncate" title={item.eans.length ? item.eans.join(', ') : undefined}>
            {item.subtitle}{opName(item.operationId) && operations.filter(o => isTracked(o.id)).length > 1 ? ` · ${opName(item.operationId)}` : ''}
            {' · '}{r.email ?? <span className="text-amber-700">pas d’e-mail</span>}
          </p>
          {progress && <p className="text-[11px] text-amber-700">Envoi incomplet : {progress}</p>}
          {r.issues.length > 0 && <p className="text-[11px] text-red-700">{r.issues.join(' · ')}</p>}
        </div>
        {!record && deferButton(item)}
        <button onClick={() => toggle(item, true)} title="Marquer comme facturé, sans rien envoyer"
          className="flex items-center gap-1 text-[11px] text-neutral-400 hover:text-emerald-700 whitespace-nowrap">
          <Check className="w-3.5 h-3.5" />Facturé
        </button>
        {item.contactId && (
          <button onClick={() => onOpenContact(item.contactId!)} className="flex items-center gap-1 text-[11px] text-neutral-500 hover:text-neutral-900 whitespace-nowrap">
            Ouvrir la fiche<ExternalLink className="w-3 h-3" />
          </button>
        )}
      </li>
    );
  };

  return (
    <div className="flex-1 overflow-y-auto p-6">
      <div className="max-w-4xl mx-auto space-y-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h3 className="text-lg font-semibold text-neutral-900">À faire en {monthLabel(month)}</h3>
            <p className="text-xs text-neutral-500 mt-0.5">
              {todoCount(list)} tâche{todoCount(list) > 1 ? 's' : ''} prête{todoCount(list) > 1 ? 's' : ''}. Récap envoyé automatiquement à info@sunisup.be le 1er de chaque mois.
            </p>
            <p className="text-[11px] text-neutral-400 mt-0.5">
              Données Supabase du {new Date(data!.loadedAt).toLocaleString('fr-BE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
              {loadError && <span className="text-red-600"> · actualisation impossible : {loadError}</span>}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => {
              load(); loadMemberTasks(); setBillingReload(v => v + 1); setFollowUpKey(v => v + 1);
              try { localStorage.removeItem(NET_CACHE_KEY); } catch { /* stockage indisponible */ }
              setNetResults(new Map()); setNetRetry(v => v + 1);
            }}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-neutral-200 text-xs text-neutral-600 hover:bg-neutral-50">
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}Actualiser
            </button>
            <button onClick={sendNow} disabled={sending} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#F8BD3D] hover:bg-[#E5A935] text-xs font-semibold text-neutral-900 disabled:opacity-50">
              {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}Envoyer le récap maintenant
            </button>
          </div>
        </div>

        {operations.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[11px] text-neutral-500 mr-0.5">Opérations incluses :</span>
            {operations.map(o => (
              <button key={o.id} onClick={() => toggleOperation(o.id)}
                className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors ${isTracked(o.id)
                  ? 'border-[#F8BD3D] bg-amber-50 text-neutral-900' : 'border-neutral-200 text-neutral-400 line-through'}`}
                title={isTracked(o.id) ? 'Cliquer pour exclure cette opération de la todo et du récap' : 'Cliquer pour inclure cette opération'}>
                {isTracked(o.id) && <Check className="w-3 h-3" />}{o.name}
              </button>
            ))}
          </div>
        )}

        <Section id="member-requests" icon={<Zap className="w-4 h-4 text-orange-600" />} title="Demandes des membres" count={memberTasks?.length ?? 0}
          empty={memberTasks === null ? 'Chargement…' : 'Aucune demande en attente.'}
          help="Demandes d’offre et intérêts pour un achat groupé envoyés depuis l’onglet Contrat du dashboard membre (un e-mail part aussi à info@sunisup.be), et contrats fournisseur qui se terminent dans les 3 mois. Cochez une tâche une fois traitée.">
          {(memberTasks ?? []).map(task => (
            <li key={task.id} className="flex items-start gap-3 px-4 py-2.5">
              <button onClick={() => completeTask(task)} title="Marquer comme traité"
                className="mt-0.5 w-4 h-4 rounded border border-neutral-300 hover:border-emerald-500 hover:bg-emerald-50 shrink-0 flex items-center justify-center group"
                aria-label={`Marquer ${task.title} comme traité`}>
                <Check className="w-3 h-3 text-emerald-600 opacity-0 group-hover:opacity-100" />
              </button>
              <div className="min-w-0 flex-1">
                <p className="text-sm text-neutral-900 flex flex-wrap items-center gap-1.5">
                  <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-800">
                    <Zap className="w-3 h-3" />{TASK_BADGES[task.badge] ?? task.badge}
                  </span>
                  <span className="font-medium">{task.title}</span>
                </p>
                <p className="text-[11px] text-neutral-500 truncate">
                  {TASK_KIND_LABELS[task.kind]} · {new Date(task.created_at).toLocaleDateString('fr-BE')}
                  {task.kind === 'contract_end' && task.details?.supplier && <> · {task.details.supplier}</>}
                  {task.details?.email && <> · {task.details.email}</>}
                  {task.details?.sites?.length ? <> · {task.details.sites.map(x => x.name).join(', ')}</> : null}
                </p>
                {task.details?.message && <p className="text-[11px] text-neutral-700 mt-0.5 whitespace-pre-line">« {task.details.message} »</p>}
              </div>
              {task.contact_id && (
                <button onClick={() => onOpenContact(task.contact_id!)} className="flex items-center gap-1 text-[11px] text-neutral-500 hover:text-neutral-900 whitespace-nowrap">
                  Ouvrir la fiche<ExternalLink className="w-3 h-3" />
                </button>
              )}
            </li>
          ))}
        </Section>

        <Section id="leads" icon={<Inbox className="w-4 h-4 text-amber-600" />} title="Leads à traiter" count={list.leads.length}
          empty="Aucun lead en attente de Sun Is Up." help="Contacts du pipeline marqués « En attente de Sun Is Up », les plus anciens d’abord.">
          {list.leads.map(i => row(i, { open: true }))}
        </Section>

        <Section id="onboarding" icon={<PartyPopper className="w-4 h-4 text-emerald-600" />} title="Onboarding des nouveaux membres" count={list.onboarding.length}
          empty="Aucun nouveau membre à accueillir." help="Ouvrez la fiche et cliquez sur « Lancer l’onboarding ».">
          {list.onboarding.map(i => row(i, { open: true }))}
        </Section>

        <Section id="invites" icon={<UserPlus className="w-4 h-4 text-blue-600" />} title="Invitations à la plateforme" count={list.invites.ready.length}
          empty="Tous les membres actifs ont un accès ou ont été invités ces 30 derniers jours."
          help="Membres actifs sans accès plateforme (même règle que le tableau de bord admin). Après l’envoi, la ligne revient au bout de 30 jours si le compte n’est toujours pas créé."
          secondary={{ label: 'Invités ces 30 derniers jours, compte pas encore créé', count: list.invites.waiting.length,
            content: list.invites.waiting.map(i => row(i, { invite: true, open: true })) }}>
          {list.invites.ready.map(i => row(i, { invite: true, open: true }))}
        </Section>

        <Section id="reports" icon={<FileText className="w-4 h-4 text-violet-600" />} title="Rapports semestriels à envoyer" count={list.reports.length}
          empty="Aucun rapport à envoyer ce mois-ci." help="Tous les 6 mois après le début du partage ; une tâche par membre, un groupe compte pour un.">
          {list.reports.map(i => row(i, { check: true, open: true }))}
        </Section>

        <Section id="reimbursements" icon={<Banknote className="w-4 h-4 text-amber-600" />} title="Remboursements des producteurs et prosumers" count={reimbursementsReady.length}
          empty="Aucun remboursement prêt : les données des périodes à rembourser ne sont pas encore toutes arrivées."
          help="Une ligne par société dans un groupe. Producteurs : mensuel dès réception des données du mois, trimestriel et semestriel par périodes fixes. Prosumers : chaque mois, ici si le solde est en leur faveur (sinon en facturation) ; « Décaler au mois suivant » regroupe 2 ou 3 mois. La tâche disparaît après l’envoi par le CRM, ou avec « Marquer comme fait »."
          waiting={list.reimbursements.waiting}
          deferred={{ count: deferredOf('reimbursement').length, content: deferredOf('reimbursement').map(deferredRow) }}
          status={prosumerStatus}
          selectAll={{
            allSelected: sendableStatements.length > 0 && selectedStatementsList.length === sendableStatements.length,
            disabled: sendableStatements.length === 0,
            onToggle: () => setSelectedStatements(selectedStatementsList.length === sendableStatements.length ? new Set() : new Set(sendableStatements.map(statementKey))),
          }}
          actions={selectedStatementsList.length > 0 && (
            <button onClick={() => setSendItems(selectedStatementsList)}
              title="Sociétés : Accountable (dépenses) et/ou e-mail. Particuliers : e-mail"
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-neutral-900 text-white text-[11px] font-semibold hover:bg-neutral-700 whitespace-nowrap">
              <Send className="w-3.5 h-3.5" />Envoyer les décomptes ({selectedStatementsList.length})
            </button>
          )}>
          {reimbursementsReady.map(i => row(i, { send: true, open: true, select: isSendableDocument(i), reimbursed: true }))}
        </Section>

        <Section id="invoices" icon={<Receipt className="w-4 h-4 text-blue-600" />} title="Facturation des consommateurs et prosumers" count={invoiceItems.length}
          empty="Aucune facturation prête : les données du mois ne sont pas encore arrivées."
          help="Chaque mois, dès réception des données, une facture par membre (une par société dans un groupe). Prosumers : ici si le solde est en faveur de la Communauté (sinon en remboursement) ; « Décaler au mois suivant » regroupe 2 ou 3 mois. Canal : fiche CRM, sinon domiciliation si mandat actif, Accountable pour une société, e-mail sinon. Rien ne part sans validation."
          waiting={[...list.invoices.waiting, ...list.prosumers.waiting]}
          deferred={{ count: deferredOf('invoice').length, content: deferredOf('invoice').map(deferredRow) }}
          status={prosumerStatus}
          selectAll={{
            allSelected: selectableBillingKeys.length > 0 && selectableBillingKeys.every(k => selectedInvoices.has(k)),
            disabled: selectableBillingKeys.length === 0,
            onToggle: () => setSelectedInvoices(selectableBillingKeys.every(k => selectedInvoices.has(k)) ? new Set() : new Set(selectableBillingKeys)),
          }}
          actions={invoiceBatches.length > 0 && (
            <div className="flex flex-wrap justify-end gap-1.5">
              {invoiceBatches.map(b => (
                <button key={`${b.operationId}|${b.period}`} onClick={() => openInvoiceTool(b.operationId, b.period, b.participantIds)}
                  title="Ouvre l’outil de factures groupées de l’opération avec ces participants pré-cochés (sans envoi)"
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-neutral-300 text-neutral-700 text-[11px] font-semibold hover:bg-neutral-50 whitespace-nowrap">
                  <Receipt className="w-3.5 h-3.5" />Outil de factures · {b.label}
                  {operations.filter(o => isTracked(o.id)).length > 1 ? ` · ${opName(b.operationId)}` : ''} ({b.count})
                </button>
              ))}
            </div>
          )}
          footer={(
            <div className="border-t border-neutral-100">
              <button onClick={() => setFollowUpOpen(v => !v)} className="w-full flex items-center gap-1.5 px-4 py-2 text-[11px] text-neutral-500 hover:bg-neutral-50">
                {followUpOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                <Landmark className="w-3.5 h-3.5" />Suivi des prélèvements : fichiers XML et statuts (encaissé, rejeté, remboursé)
              </button>
              {followUpOpen && <SepaFollowUp refreshKey={followUpKey} />}
            </div>
          )}>
          {billingError && <li className="px-4 py-2 text-xs text-red-700">Facturation : {billingError}</li>}
          {!billingData && !billingError && (
            <li className="flex items-center gap-1.5 px-4 py-3 text-xs text-neutral-400"><Loader2 className="w-3.5 h-3.5 animate-spin" />Canaux de facturation…</li>
          )}
          {billingData && BILLING_CHANNELS.map(ch => {
            const rows = billingRows.filter(r => r.channel === ch);
            const selected = rows.filter(r => r.issues.length === 0 && selectedInvoices.has(r.key));
            const Icon = CHANNEL_ICONS[ch];
            return (
              <li key={ch}>
                <div className="flex items-start gap-2 px-4 py-2 bg-neutral-50/70">
                  <Icon className="w-3.5 h-3.5 mt-0.5 text-neutral-500 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold text-neutral-800">{BILLING_CHANNEL_LABELS[ch]} <span className="ml-1 font-normal text-neutral-500">{rows.length}</span></p>
                    <p className="text-[10px] text-neutral-500">{CHANNEL_HELP[ch]}</p>
                  </div>
                  <button onClick={() => setBillingRun({ channel: ch, rows: selected })} disabled={selected.length === 0}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-neutral-900 text-white text-[11px] font-semibold hover:bg-neutral-700 whitespace-nowrap disabled:opacity-30">
                    <Send className="w-3.5 h-3.5" />{CHANNEL_BUTTONS[ch]} ({selected.length})
                  </button>
                </div>
                {rows.length === 0
                  ? <p className="px-4 py-2 text-[11px] text-neutral-400">Aucune facture pour ce canal.</p>
                  : <ul className="divide-y divide-neutral-100">{rows.map(r => billingRowView(r))}</ul>}
              </li>
            );
          })}
        </Section>

        <p className="text-[11px] text-neutral-400 flex items-center gap-1.5">
          <CalendarClock className="w-3.5 h-3.5" />Opérations suivies et points de départ : CRM → Paramètres → Todolist.
        </p>
      </div>
      {sendItems && sendItems.length > 0 && (
        <SendDocumentsDialog items={sendItems} operations={operations} onClose={() => setSendItems(null)} onSent={markDoneLocal} />
      )}
      {billingRun && billingRun.rows.length > 0 && (
        <BillingRunDialog
          channel={billingRun.channel}
          rows={billingRun.rows}
          operations={operations}
          onClose={() => { setBillingRun(null); setBillingReload(v => v + 1); setFollowUpKey(v => v + 1); }}
          onRecords={mergeRecords}
          onDone={markDoneLocal}
        />
      )}
    </div>
  );
}

const TODO_OPEN_KEY = 'siu-crm-todo-open';
const statementKey = (item: TodoItem) => `${item.kind}|${item.ref}|${item.period}`;
const toggleIn = (set: Set<string>, key: string) => {
  const next = new Set(set);
  if (next.has(key)) next.delete(key); else next.add(key);
  return next;
};
function readTodoOpen(): string[] {
  try { const v = JSON.parse(localStorage.getItem(TODO_OPEN_KEY) ?? '[]'); return Array.isArray(v) ? v : []; } catch { return []; }
}

/** Rubrique de la todo : repliée par défaut (comme CRM → Paramètres), état mémorisé par navigateur. */
function Section({ id, icon, title, count, empty, help, waiting, actions, secondary, footer, selectAll, deferred, status, children }: {
  id: string; icon: ReactNode; title: string; count: number; empty: string; help: string; waiting?: TodoItem[]; actions?: ReactNode;
  /** Prosumers décalés au mois suivant (liste repliée, en plus des données en attente). */
  deferred?: { count: number; content: ReactNode };
  /** Lignes d'état affichées en tête de liste (ex. calcul du solde des prosumers). */
  status?: ReactNode;
  /** Bascule « Tout sélectionner » affichée sous le titre, désactivée s'il n'y a rien à sélectionner. */
  selectAll?: { allSelected: boolean; disabled: boolean; onToggle: () => void };
  /** Bloc affiché sous la liste, même vide (ex. suivi des prélèvements). */
  footer?: ReactNode;
  /** Liste secondaire repliée (ex. membres déjà invités). */
  secondary?: { label: string; content: ReactNode; count: number };
  children: ReactNode;
}) {
  const [showSecondary, setShowSecondary] = useState(false);
  const [showWaiting, setShowWaiting] = useState(false);
  const [showDeferred, setShowDeferred] = useState(false);
  const [open, setOpen] = useState(() => readTodoOpen().includes(id));
  const toggleOpen = () => setOpen(o => {
    const next = !o;
    try {
      const list = readTodoOpen().filter(k => k !== id);
      localStorage.setItem(TODO_OPEN_KEY, JSON.stringify(next ? [...list, id] : list));
    } catch { /* stockage indisponible */ }
    return next;
  });
  const waitingCount = waiting?.length ?? 0;
  const groups = useMemo(() => {
    const map = new Map<string, { label: string; missing: string; items: TodoItem[] }>();
    for (const i of waiting ?? []) {
      const k = `${i.period}|${i.missingMonth}`;
      const g = map.get(k) ?? { label: i.periodLabel, missing: i.missingMonth ?? '', items: [] };
      g.items.push(i);
      map.set(k, g);
    }
    return Array.from(map.values());
  }, [waiting]);

  return (
    <section className={`bg-white border rounded-xl overflow-hidden transition-colors ${open ? 'border-neutral-300 shadow-sm' : 'border-neutral-200 hover:border-neutral-300'}`}>
      <div className="flex items-center justify-between gap-2 px-4 py-3">
        <button type="button" onClick={toggleOpen} aria-expanded={open} aria-controls={`todo-${id}`}
          className="flex-1 min-w-0 flex items-center gap-2 text-left">
          <ChevronDown className={`w-4 h-4 text-neutral-400 shrink-0 transition-transform ${open ? '' : '-rotate-90'}`} />
          {icon}
          <h4 className="text-sm font-semibold text-neutral-900 truncate">{title}</h4>
          <span className={`shrink-0 min-w-[1.5rem] px-1.5 py-0.5 rounded-full text-center text-[11px] font-semibold ${count > 0 ? 'bg-amber-100 text-amber-800' : 'bg-neutral-100 text-neutral-400'}`}>{count}</span>
          {!open && waitingCount > 0 && <span className="shrink-0 text-[11px] text-neutral-400">· {waitingCount} en attente des données</span>}
          {!open && (deferred?.count ?? 0) > 0 && <span className="shrink-0 text-[11px] text-neutral-400">· {deferred!.count} décalée{deferred!.count > 1 ? 's' : ''}</span>}
        </button>
        {open && actions && <div className="shrink-0">{actions}</div>}
      </div>
      {open && (<div id={`todo-${id}`} className="border-t border-neutral-100">
      {selectAll && (
        <div className="px-4 pt-2.5">
          <button type="button" onClick={selectAll.onToggle} disabled={selectAll.disabled}
            className="text-[11px] font-semibold text-neutral-700 hover:text-neutral-900 underline-offset-2 hover:underline disabled:opacity-40 disabled:no-underline">
            {selectAll.allSelected ? 'Tout désélectionner' : 'Tout sélectionner'}
          </button>
        </div>
      )}
      <p className="px-4 pt-2 pb-2 text-[11px] text-neutral-500 bg-neutral-50/60 border-b border-neutral-100">{help}</p>
      {status && <ul className="divide-y divide-neutral-100">{status}</ul>}
      {count === 0 ? <p className="px-4 py-3 text-xs text-neutral-400">{empty}</p> : <ul className="divide-y divide-neutral-100">{children}</ul>}
      {secondary && secondary.count > 0 && (
        <div className="border-t border-neutral-100">
          <button onClick={() => setShowSecondary(v => !v)} className="w-full flex items-center gap-1.5 px-4 py-2 text-[11px] text-neutral-500 hover:bg-neutral-50">
            {showSecondary ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
            <Hourglass className="w-3.5 h-3.5" />{secondary.label} : {secondary.count}
          </button>
          {showSecondary && <ul className="divide-y divide-neutral-100 bg-neutral-50/40">{secondary.content}</ul>}
        </div>
      )}
      {deferred && deferred.count > 0 && (
        <div className="border-t border-neutral-100">
          <button onClick={() => setShowDeferred(v => !v)} className="w-full flex items-center gap-1.5 px-4 py-2 text-[11px] text-neutral-500 hover:bg-neutral-50">
            {showDeferred ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
            <CalendarPlus className="w-3.5 h-3.5" />Décalées au mois suivant : {deferred.count}
          </button>
          {showDeferred && <ul className="divide-y divide-neutral-100 bg-neutral-50/40">{deferred.content}</ul>}
        </div>
      )}
      {groups.length > 0 && (
        <div className="border-t border-neutral-100">
          <button onClick={() => setShowWaiting(v => !v)} className="w-full flex items-center gap-1.5 px-4 py-2 text-[11px] text-neutral-500 hover:bg-neutral-50">
            {showWaiting ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
            <Hourglass className="w-3.5 h-3.5" />En attente des données : {waiting!.length}
          </button>
          {showWaiting && (
            <ul className="px-4 pb-3 space-y-1.5">
              {groups.map(g => (
                <li key={`${g.label}|${g.missing}`} className="text-[11px] text-neutral-600">
                  <span className="font-medium text-neutral-700">{g.label}</span> · {g.items.length} ligne{g.items.length > 1 ? 's' : ''}, données de {monthLabel(g.missing)} attendues
                  <span className="block text-neutral-400 truncate" title={g.items.map(i => i.title).join(', ')}>{g.items.map(i => i.title).join(', ')}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {footer}
      </div>)}
    </section>
  );
}
